import {
  UserPlus,
  CreditCard,
  Clock,
  XCircle,
  Calendar,
  ShoppingCart,
  CheckCircle,
  Ban,
  BookOpen,
  FileCheck,
  Users,
  Sparkles,
  Ticket,
  KeyRound,
  Unlock,
  UsersRound,
  UserMinus,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import type { TriggerEventType } from '../types';

export type EventCategory = 'users' | 'subscriptions' | 'orders' | 'courses' | 'channels' | 'family' | 'access';

export interface TriggerEventConfig {
  label: string;
  description: string;
  icon: LucideIcon;
  category: EventCategory;
  examples: string[];
}

export const TRIGGER_EVENTS: Record<TriggerEventType, TriggerEventConfig> = {
  USER_REGISTERED: {
    label: 'Регистрация пользователя',
    description: 'Новый пользователь зарегистрировался в системе',
    icon: UserPlus,
    category: 'users',
    examples: ['Приветственное сообщение', 'Бонус за регистрацию'],
  },
  SUBSCRIPTION_PURCHASED: {
    label: 'Покупка подписки',
    description: 'Пользователь оплатил подписку (до активации)',
    icon: CreditCard,
    category: 'subscriptions',
    examples: ['Благодарность за покупку', 'Инструкции по началу работы'],
  },
  SUBSCRIPTION_STARTED: {
    label: 'Активация подписки',
    description: 'Подписка пользователя активирована',
    icon: CheckCircle,
    category: 'subscriptions',
    examples: ['Доступ к материалам открыт', 'Приглашение в чат'],
  },
  SUBSCRIPTION_EXPIRING: {
    label: 'Подписка скоро истечёт',
    description: 'До окончания подписки осталось 5, 3 или 1 день (payload: daysLeft / daysRemaining). Условия: { "daysLeft": 5 } и т.п.',
    icon: Clock,
    category: 'subscriptions',
    examples: ['Напоминание за 5/3/1 день', 'Скидка на продление'],
  },
  SUBSCRIPTION_EXPIRED: {
    label: 'Подписка истекла',
    description: 'Подписка пользователя закончилась',
    icon: XCircle,
    category: 'subscriptions',
    examples: ['Предложение возобновить', 'Опрос причин ухода'],
  },
  SUBSCRIPTION_EXPIRATION: {
    label: 'Дата истечения подписки',
    description: 'Наступила конкретная дата окончания подписки',
    icon: Calendar,
    category: 'subscriptions',
    examples: ['Автоматическое отключение', 'Финальное напоминание'],
  },
  ORDER_CREATED: {
    label: 'Создан заказ',
    description: 'Пользователь создал новый заказ',
    icon: ShoppingCart,
    category: 'orders',
    examples: ['Подтверждение заказа', 'Инструкции по оплате'],
  },
  ORDER_PAID: {
    label: 'Заказ оплачен',
    description: 'Заказ успешно оплачен',
    icon: CheckCircle,
    category: 'orders',
    examples: ['Чек об оплате', 'Благодарность за покупку'],
  },
  ORDER_CANCELLED: {
    label: 'Заказ отменён',
    description: 'Заказ был отменён',
    icon: Ban,
    category: 'orders',
    examples: ['Подтверждение отмены', 'Возврат средств'],
  },
  COURSE_COMPLETED: {
    label: 'Курс завершён',
    description: 'Пользователь завершил прохождение курса',
    icon: BookOpen,
    category: 'courses',
    examples: ['Поздравление', 'Сертификат', 'Рекомендации следующих курсов'],
  },
  HOMEWORK_SUBMITTED: {
    label: 'Домашнее задание отправлено',
    description: 'Пользователь отправил домашнее задание на проверку',
    icon: FileCheck,
    category: 'courses',
    examples: ['Уведомление куратору', 'Подтверждение ученику'],
  },
  NEW_COURSE_AVAILABLE: {
    label: 'Новый курс доступен',
    description: 'Для пользователя стал доступен новый курс',
    icon: Sparkles,
    category: 'courses',
    examples: ['Анонс нового курса', 'Приглашение к прохождению'],
  },
  COURSE_ACCESS_EXPIRED: {
    label: 'Доступ к курсу истёк',
    description: 'Срок доступа пользователя к курсу закончился',
    icon: KeyRound,
    category: 'courses',
    examples: ['Предложение продлить доступ', 'Информация о завершении'],
  },
  COURSE_DRIP_RELEASED: {
    label: 'Материал курса разблокирован',
    description: 'Новый материал стал доступен по расписанию (drip content)',
    icon: Unlock,
    category: 'courses',
    examples: ['Уведомление о новом уроке', 'Напоминание продолжить'],
  },
  CHANNEL_PASS_LIMIT_REACHED: {
    label: 'Достигнут лимит пропуска',
    description: 'Использован лимит пропуска канала',
    icon: Ticket,
    category: 'channels',
    examples: ['Уведомление о лимите', 'Деактивация пропуска'],
  },
  CHANNEL_PASS_EXPIRED: {
    label: 'Пропуск канала истёк',
    description: 'Срок действия пропуска на канал закончился',
    icon: XCircle,
    category: 'channels',
    examples: ['Предложение продлить', 'Отзыв приглашения'],
  },
  FAMILY_MEMBER_ADDED: {
    label: 'Добавлен член семьи',
    description: 'К семейной подписке добавлен новый участник',
    icon: UsersRound,
    category: 'family',
    examples: ['Приветствие новому участнику', 'Уведомление владельцу'],
  },
  FAMILY_MEMBER_REMOVED: {
    label: 'Удалён член семьи',
    description: 'Участник удалён из семейной подписки',
    icon: UserMinus,
    category: 'family',
    examples: ['Уведомление об удалении', 'Отзыв доступов'],
  },
  FAMILY_SLOTS_CHANGED: {
    label: 'Изменено количество слотов',
    description: 'Изменилось количество слотов в семейной подписке',
    icon: SlidersHorizontal,
    category: 'family',
    examples: ['Уведомление об изменении', 'Информация о новых слотах'],
  },
  ACCESS_GRANTED: {
    label: 'Выдан доступ (low-level)',
    description: 'Любая выдача доступа (курс, канал, модуль). Срабатывает на каждый grant — включая бонусные/реферальные/ручные. Для агрегированной подписки используйте SUBSCRIPTION_STARTED.',
    icon: Unlock,
    category: 'access',
    examples: ['Уведомление о новом доступе', 'Подсказка как начать пользоваться'],
  },
};

export const EVENT_CATEGORIES: Record<EventCategory, { label: string; icon: LucideIcon }> = {
  users: { label: 'Пользователи', icon: Users },
  subscriptions: { label: 'Подписки', icon: CreditCard },
  orders: { label: 'Заказы', icon: ShoppingCart },
  courses: { label: 'Курсы', icon: BookOpen },
  channels: { label: 'Каналы', icon: Ticket },
  family: { label: 'Семейные подписки', icon: UsersRound },
  access: { label: 'Доступы', icon: Unlock },
};

export function getEventsByCategory(category: EventCategory): Array<{ key: TriggerEventType; config: TriggerEventConfig }> {
  return (Object.entries(TRIGGER_EVENTS) as Array<[TriggerEventType, TriggerEventConfig]>)
    .filter(([, config]) => config.category === category)
    .map(([key, config]) => ({ key, config }));
}

export function getEventsGroupedByCategory(): Array<{
  category: EventCategory;
  label: string;
  icon: LucideIcon;
  events: Array<{ key: TriggerEventType; config: TriggerEventConfig }>;
}> {
  return (Object.entries(EVENT_CATEGORIES) as Array<[EventCategory, { label: string; icon: LucideIcon }]>).map(
    ([category, { label, icon }]) => ({
      category,
      label,
      icon,
      events: (Object.entries(TRIGGER_EVENTS) as Array<[TriggerEventType, TriggerEventConfig]>)
        .filter(([, config]) => config.category === category)
        .map(([key, config]) => ({ key, config })),
    })
  );
}
